import './Group.css';

import noop from 'lodash/noop';
import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import {
  MatchProps,
  MatchPropTypes,
  groups as groupLetters,
} from '@models/Match';
import Team from '@views/Team';
import Score from '@views/Score';
import GroupTop from '@views/GroupTop';
import { Table } from 'antd';

export const scoreRenderer =
  (canEdit, fieldName, onChange) =>
  (value, { mid, editable, assertions = [] }) =>
    canEdit && editable ? (
      <Score
        value={value}
        onChange={(newValue) => onChange(mid, { [fieldName]: newValue })}
      />
    ) : (
      <span
        className={`score-value${
          mapAssertionsToCssClasses(assertions).includes('groups-score')
            ? ' score-value--exact'
            : ''
        }`}
      >
        {value}
      </span>
    );

const hasAssertionClass = (assertions = [], cssClass) =>
  mapAssertionsToCssClasses(assertions).includes(cssClass);

const getTeamWinnerClass = (match = {}, side) => {
  const assertions = get(match, 'assertions', []);
  const hasWinner = hasAssertionClass(assertions, 'groups-winner');
  const hasExact = hasAssertionClass(assertions, 'groups-score');
  if (!hasWinner || hasExact) return '';
  return match.winner === side ? 'team-cell--winner' : '';
};

export const getColumns = (canEdit, onChange) => [
  {
    dataIndex: MatchProps.TeamHome,
    className: 'column-team',
    key: 'team-home',
    align: 'right',
    render: (code, match) => (
      <span className={`team-cell ${getTeamWinnerClass(match, 'home')}`.trim()}>
        <Team code={code} align="right" />
      </span>
    ),
  },
  {
    dataIndex: MatchProps.ScoreHome,
    className: canEdit ? 'column-score-editable' : 'column-score',
    key: 'home-score',
    align: 'center',
    render: scoreRenderer(canEdit, MatchProps.ScoreHome, onChange),
  },
  {
    dataIndex: MatchProps.ScoreAway,
    className: canEdit ? 'column-score-editable' : 'column-score',
    key: 'away-score',
    align: 'center',
    render: scoreRenderer(canEdit, MatchProps.ScoreAway, onChange),
  },
  {
    dataIndex: MatchProps.TeamAway,
    className: 'column-team',
    key: 'team-away',
    align: 'left',
    render: (code, match) => (
      <span className={`team-cell ${getTeamWinnerClass(match, 'away')}`.trim()}>
        <Team code={code} />
      </span>
    ),
  },
];

const title = (group) => () => `Grupo ${group.toUpperCase()}`;

// Mapeo de assertions v2 a clases CSS v1
const mapAssertionsToCssClasses = (assertions = []) => {
  return assertions.map(assertion => {
    // Sistema v2 -> Sistema v1 (CSS)
    if (assertion === 'resultado-correcto') return 'groups-winner';
    if (assertion === 'resultado-exacto') return 'groups-winner groups-score';
    if (assertion === 'equipo-clasificado') return 'bracket-winner';
    if (assertion === 'resultado-exacto-eliminatoria') return 'bracket-winner bracket-score';
    // Sistema v1 (mantener como está)
    return assertion;
  }).join(' ');
};

export const getMatchClass = (match) =>
  `${get(match, 'editable') ? 'match-editable ' : ''}${mapAssertionsToCssClasses(get(
    match,
    'assertions',
    []
  ))} ${get(match, 'isToday') ? 'match-today' : ''}`;

const Group = ({ matches, group, columns, editable, onChange }) => (
  <div className={`group-table group-${group}`}>
    <Table
      columns={columns || getColumns(editable, onChange)}
      dataSource={matches}
      rowKey="mid"
      rowClassName={getMatchClass}
      size="small"
      bordered={false}
      pagination={false}
      showHeader={false}
      footer={() => <GroupTop group={group} />}
      title={title(group)}
    />
  </div>
);

Group.displayName = 'Group';
Group.propTypes = {
  group: PropTypes.oneOf(groupLetters),
  editable: PropTypes.bool,
  matches: PropTypes.arrayOf(
    PropTypes.shape({
      key: MatchPropTypes[MatchProps.MatchId],
      ...MatchPropTypes,
    })
  ),
  columns: PropTypes.array,
  onChange: PropTypes.func,
};
Group.defaultProps = {
  editable: false,
  onChange: noop,
};

export default Group;
